import { useEffect, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Info, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  dismissNotification,
  listNotifications,
  markAllNotificationsSeen,
  markNotificationSeen,
  refreshInvoicingWarnings,
  type NotificationRecord,
  type NotificationsResponse,
} from '@/features/planning/api';
import type { Language } from '@/types';

interface Props {
  language: Language;
}

const severityMeta = {
  info: { icon: Info, variant: 'info' as const },
  warning: { icon: AlertTriangle, variant: 'warning' as const },
  error: { icon: XCircle, variant: 'destructive' as const },
};

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'nyss';
  if (min < 60) return `${min} min sedan`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} tim sedan`;
  const d = Math.floor(h / 24);
  return `${d} dagar sedan`;
}

export function NotificationsWorkspace({ language }: Props) {
  const { session, profile } = useAuth();
  const token = session?.access_token ?? null;
  const isAdmin = profile?.role === 'admin';

  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listNotifications(token, includeDismissed);
      setData(res);
    } catch (err) {
      console.error('Failed to load notifications', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, includeDismissed]);

  const handleRefreshWarnings = async () => {
    if (!token) return;
    setWorking(true);
    try {
      await refreshInvoicingWarnings({}, token);
      await load();
    } finally {
      setWorking(false);
    }
  };

  const handleMarkAllSeen = async () => {
    if (!token || !data) return;
    setWorking(true);
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    setData({
      ...data,
      counts: { ...data.counts, unseen: 0 },
      notifications: data.notifications.map((n) =>
        n.seen_at ? n : { ...n, seen_at: now }
      ),
    });
    try {
      await markAllNotificationsSeen(token);
    } catch (err) {
      console.error('Mark all seen failed', err);
      setData(prev);
      await load();
    } finally {
      setWorking(false);
    }
  };

  const handleDismiss = async (n: NotificationRecord) => {
    if (!token || !data) return;
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    if (includeDismissed) {
      setData({
        ...data,
        notifications: data.notifications.map((item) =>
          item.id === n.id ? { ...item, dismissed_at: now } : item
        ),
      });
    } else {
      setData({
        ...data,
        notifications: data.notifications.filter((item) => item.id !== n.id),
      });
    }
    try {
      await dismissNotification(n.id, token);
    } catch (err) {
      console.error('Dismiss failed', err);
      setData(prev);
    }
  };

  const handleSeen = async (n: NotificationRecord) => {
    if (!token || !data || n.seen_at) return;
    // Optimistic update
    const prev = data;
    const now = new Date().toISOString();
    setData({
      ...data,
      counts: { ...data.counts, unseen: Math.max(0, data.counts.unseen - 1) },
      notifications: data.notifications.map((item) =>
        item.id === n.id ? { ...item, seen_at: now } : item
      ),
    });
    try {
      await markNotificationSeen(n.id, token);
    } catch {
      setData(prev);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {language === 'sv' ? 'Notifieringar' : 'Notifications'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {language === 'sv'
              ? 'Varningar och påminnelser från systemet.'
              : 'System warnings and reminders.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setIncludeDismissed((prev) => !prev)}>
            {includeDismissed
              ? language === 'sv' ? 'Göm stängda' : 'Hide dismissed'
              : language === 'sv' ? 'Visa stängda' : 'Show dismissed'}
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleRefreshWarnings} disabled={working}>
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1">{language === 'sv' ? 'Sök nya' : 'Scan now'}</span>
            </Button>
          )}
          <Button size="sm" onClick={handleMarkAllSeen} disabled={working}>
            <CheckCircle2 className="h-4 w-4 mr-1" />
            {language === 'sv' ? 'Markera alla lästa' : 'Mark all read'}
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3">
          <KpiCard label={language === 'sv' ? 'Olästa' : 'Unread'} value={data.counts.unseen} />
          <KpiCard label={language === 'sv' ? 'Varningar' : 'Warnings'} value={data.counts.warning} />
          <KpiCard label={language === 'sv' ? 'Fel' : 'Errors'} value={data.counts.error} />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.notifications.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {language === 'sv' ? 'Inga notifieringar.' : 'No notifications.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.notifications.map((n) => {
            const meta = severityMeta[n.severity];
            const Icon = meta.icon;
            const isDismissed = Boolean(n.dismissed_at);
            return (
              <Card
                key={n.id}
                className={isDismissed ? 'opacity-60' : ''}
              >
                <CardContent className="p-4 flex items-start gap-3">
                  <Icon className="h-5 w-5 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{n.title}</span>
                      <Badge variant={meta.variant}>{n.severity}</Badge>
                      {!n.seen_at && !isDismissed && (
                        <Badge variant="default" className="text-xs">
                          {language === 'sv' ? 'Nytt' : 'New'}
                        </Badge>
                      )}
                    </div>
                    {n.body && <p className="text-sm text-muted-foreground mt-1">{n.body}</p>}
                    <p className="text-xs text-muted-foreground mt-1">{formatRelative(n.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!n.seen_at && !isDismissed && (
                      <Button variant="ghost" size="sm" onClick={() => void handleSeen(n)}>
                        <Check className="h-4 w-4 mr-1" />
                        {language === 'sv' ? 'Markera läst' : 'Mark read'}
                      </Button>
                    )}
                    {!isDismissed && (
                      <Button variant="ghost" size="sm" onClick={() => void handleDismiss(n)}>
                        {language === 'sv' ? 'Stäng' : 'Dismiss'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
